import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { EPropertyCascaderItemType } from 'common/const';
import { AppDispatch, RootState, store } from 'app/store';
import {
  IProperty,
  IPropertyCascaderItem,
  IPropertyCascaderItemCreateOptionPayload,
  IPropertyCascaderItemCreatePropertyPayload,
  IPropertyCascaderItemDeleteOptionPayload,
  IPropertyCascaderItemDeletePropertyPayload,
  IPropertyCascaderItemSelectOptionPayload,
  IPropertyCascaderItemSelectPropertyPayload,
  IPropertyCascaderItemSubscribePropertyPayload,
  IPropertyCascaderItemUnsubscribePropertyPayload,
  IPropertyListParams,
  IUpdatePropertyPayload,
  ISubcategoryCreatePayload,
  ISubcategoryDeletePayload,
  IPropertyListByCategoryPayload,
} from 'entities/Property/Property.models';
import { propertyTransport } from 'entities/Property/Property.transport';
import { createCascaderItem, getPropertyPayload } from 'entities/Property/Property.helper';

interface IState {
  propertyList: IProperty[];
  propertyCount: number;
  isPropertyLoading: boolean;
  propertyError: string | null;
  propertyCascaderItems: IPropertyCascaderItem[];
  selectedPropertyId: number | null;
  propertyCascaderError: string | null;
}

const initialState: IState = {
  propertyList: [],
  propertyCount: 0,
  isPropertyLoading: false,
  propertyError: null,
  propertyCascaderItems: [],
  selectedPropertyId: null,
  propertyCascaderError: null,
};

export const propertySlice = createSlice({
  name: 'property',
  initialState,
  reducers: {
    setPropertyList(state, action: PayloadAction<IProperty[]>) {
      state.propertyList = action.payload;
    },
    setPropertyCount(state, action: PayloadAction<number>) {
      state.propertyCount = action.payload;
    },
    setIsPropertyLoading(state, action: PayloadAction<boolean>) {
      state.isPropertyLoading = action.payload;
    },
    setPropertyError(state, action: PayloadAction<string | null>) {
      state.propertyError = action.payload;
    },
    addPropertyListItem(state, action: PayloadAction<IProperty>) {
      state.propertyList.push(action.payload);
    },
    patchPropertyListItem(state, action: PayloadAction<IProperty>) {
      const property = state.propertyList.find((item) => item.id === action.payload.id);

      if (!property) {
        return;
      }

      Object.assign(property, action.payload);
    },
    removePropertyListItem(state, action: PayloadAction<number>) {
      state.propertyList = state.propertyList.filter((item) => item.id !== action.payload);
    },
    patchPropertyListItemWithParent(state, action: PayloadAction<{ property: IProperty; parent: IProperty }>) {
      const property = state.propertyList.find((item) => item.id === action.payload.property.id);
      const parent = state.propertyList.find((item) => item.id === action.payload.parent.id);

      if (property) {
        Object.assign(property, action.payload.property);
      }

      if (parent) {
        Object.assign(parent, action.payload.parent);
      }
    },
    filterPropertyListItemShowConditions(state, action: PayloadAction<{ propertyId: number; value: string }>) {
      state.propertyList = state.propertyList.map((item) => {
        return {
          ...item,
          showCondition: item.showCondition?.map((i) => {
            if (i.propertyId === action.payload.propertyId) {
              return {
                ...i,
                values: i.values.filter((v) => v !== action.payload.value),
              };
            }

            return i;
          }),
        };
      });
    },
    setPropertyCascaderItems(state, action: PayloadAction<IPropertyCascaderItem[]>) {
      state.propertyCascaderItems = action.payload;
    },
    setSelectedPropertyId(state, action: PayloadAction<number | null>) {
      state.selectedPropertyId = action.payload;
    },
    setPropertyCascaderError(state, action: PayloadAction<string | null>) {
      state.propertyCascaderError = action.payload;
    },
    changePropertyCascaderItemSelectedPropertyId(state, action: PayloadAction<IPropertyCascaderItemSelectPropertyPayload>) {
      const index = state.propertyCascaderItems.findIndex((item) => item.id === action.payload.cascaderItemId);

      if (index === -1) {
        return;
      }

      state.propertyCascaderItems = state.propertyCascaderItems.slice(0, index + 1);
      state.propertyCascaderItems[index].selectedPropertyId = action.payload.propertyId;
      state.selectedPropertyId = action.payload.propertyId;
    },
    changePropertyCascaderItemSelectedOptionId(state, action: PayloadAction<{ valueId: number; cascaderItemId: string }>) {
      const index = state.propertyCascaderItems.findIndex((item) => item.id === action.payload.cascaderItemId);

      if (index === -1) {
        return;
      }

      state.propertyCascaderItems = state.propertyCascaderItems.slice(0, index + 1);
      state.propertyCascaderItems[index].selectedOptionId = action.payload.valueId;
      state.selectedPropertyId = null;
    },
    addPropertyCascaderItem(state, action: PayloadAction<IPropertyCascaderItem>) {
      state.propertyCascaderItems.push(action.payload);
    },
    filterPropertyCascaderItems(state, action: PayloadAction<string>) {
      const index = state.propertyCascaderItems.findIndex((item) => item.id === action.payload);

      if (index === -1) {
        return;
      }

      state.propertyCascaderItems = state.propertyCascaderItems.slice(0, index + 1);
      state.propertyCascaderItems[index].selectedPropertyId = null;
      state.selectedPropertyId = null;
    },
    setPropertyCascaderItemError(state, action: PayloadAction<{ error: string | null; cascaderItemId: string }>) {
      const cascaderItem = state.propertyCascaderItems.find((item) => item.id === action.payload.cascaderItemId);

      if (!cascaderItem) {
        return;
      }

      cascaderItem.error = action.payload.error;
    },
    patchPropertyCascaderItemWithOptions(state, action: PayloadAction<IProperty>) {
      const item = state.propertyCascaderItems.find(
        (i) => i.propertyId === action.payload.id && i.type === EPropertyCascaderItemType.Options,
      );

      if (!item) {
        return;
      }

      item.value = action.payload.isCategory
        ? `${action.payload.displayName} | подкатегории`
        : `Список значений для ${action.payload.displayName}`;
      item.propertyType = action.payload.type;
    },
    patchPropertyCascaderItemWithProperties(state, action: PayloadAction<{ property: IProperty; parent: IProperty }>) {
      const { property, parent } = action.payload;
      const newParentOption = parent.values.find((item) => item.value === property.displayName);

      state.propertyCascaderItems = state.propertyCascaderItems.map((item) => {
        if (item.propertyId === property.parentId && item.type === EPropertyCascaderItemType.Options) {
          return {
            ...item,
            selectedOptionId: newParentOption?.valueId,
          };
        }

        if (item.propertyId === property.parentId && item.type === EPropertyCascaderItemType.Properties) {
          return {
            ...item,
            value: property.displayName,
          };
        }

        if (item.propertyId === property.id && item.type === EPropertyCascaderItemType.Options) {
          return {
            ...item,
            value: `${property.displayName} | подкатегории`,
          };
        }

        return item;
      });
    },
    removePropertyCascaderItemWithProperties(state, action: PayloadAction<IProperty>) {
      const cascaderItemIndex = state.propertyCascaderItems.findIndex((item) => {
        return (
          item.propertyId === action.payload.parentId &&
          item.type === EPropertyCascaderItemType.Properties &&
          item.value === action.payload.displayName
        );
      });
      const cascaderItemExists = cascaderItemIndex > 0;

      state.propertyCascaderItems = !cascaderItemExists
        ? state.propertyCascaderItems
        : state.propertyCascaderItems.filter((_, index) => index < cascaderItemIndex);
      state.selectedPropertyId = cascaderItemExists ? null : state.selectedPropertyId;
      state.propertyCascaderError = cascaderItemExists ? null : state.propertyCascaderError;
    },
  },
});

export const {
  setPropertyList,
  setPropertyCount,
  setIsPropertyLoading,
  setPropertyError,
  addPropertyListItem,
  patchPropertyListItem,
  removePropertyListItem,
  patchPropertyListItemWithParent,
  filterPropertyListItemShowConditions,
  setPropertyCascaderItems,
  setSelectedPropertyId,
  setPropertyCascaderError,
  changePropertyCascaderItemSelectedPropertyId,
  changePropertyCascaderItemSelectedOptionId,
  addPropertyCascaderItem,
  filterPropertyCascaderItems,
  setPropertyCascaderItemError,
  patchPropertyCascaderItemWithOptions,
  patchPropertyCascaderItemWithProperties,
  removePropertyCascaderItemWithProperties,
} = propertySlice.actions;

export const selectPropertyList = (state: RootState) => state.property.propertyList;
export const selectPropertyCount = (state: RootState) => state.property.propertyCount;
export const selectIsPropertyLoading = (state: RootState) => state.property.isPropertyLoading;
export const selectPropertyError = (state: RootState) => state.property.propertyError;
export const selectPropertyCascaderItems = (state: RootState) => state.property.propertyCascaderItems;
export const selectSelectedPropertyId = (state: RootState) => state.property.selectedPropertyId;
export const selectPropertyCascaderError = (state: RootState) => state.property.propertyCascaderError;

export const propertyReducer = propertySlice.reducer;

export const getPropertyList = (params: IPropertyListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsPropertyLoading(true));

  try {
    const response = await propertyTransport.get(params);
    dispatch(setPropertyList(response.data));
    dispatch(setPropertyCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsPropertyLoading(false));
  }
};

export const getPropertyListByCategory = (payload: IPropertyListByCategoryPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsPropertyLoading(true));

  try {
    const response = await propertyTransport.getByCategory(payload);
    dispatch(setPropertyList(response.data));
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsPropertyLoading(false));
  }
};

export const selectSubcategory = (id: number) => (dispatch: AppDispatch) => {
  const state = store.getState();
  const property = state.property.propertyList.find((propertyItem) => propertyItem.id === id);

  if (property) {
    const rootCascaderItem = createCascaderItem.rootItem(property);
    dispatch(setPropertyCascaderItems([rootCascaderItem]));
    dispatch(setSelectedPropertyId(null));
  }
};

export const createSubcategory = (payload: ISubcategoryCreatePayload) => async (dispatch: AppDispatch) => {
  const { name, parentId } = payload;
  const state = store.getState();

  dispatch(setPropertyError(null));
  dispatch(setIsPropertyLoading(true));

  try {
    const createPayload = getPropertyPayload.createSubcategory(name, parentId);
    const createdProperty = await propertyTransport.create(createPayload);
    dispatch(addPropertyListItem(createdProperty));

    if (parentId) {
      const parentProperty = state.property.propertyList.find((property) => {
        return property.id === parentId;
      }) as IProperty;
      const updatePayload = getPropertyPayload.addPropertyValue(name, parentProperty);
      const updatedProperty = await propertyTransport.update(updatePayload);
      dispatch(patchPropertyListItem(updatedProperty));
    }

    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setPropertyError);
  } finally {
    dispatch(setIsPropertyLoading(false));
  }
};

export const deleteSubcategory = (payload: ISubcategoryDeletePayload) => async (dispatch: AppDispatch) => {
  const { id, isSelected } = payload;
  const state = store.getState();

  dispatch(setPropertyError(null));
  dispatch(setIsPropertyLoading(true));

  try {
    const deletedProperty = await propertyTransport.delete({ id });
    dispatch(removePropertyListItem(deletedProperty.id));

    if (deletedProperty.isCategory) {
      dispatch(removePropertyCascaderItemWithProperties(deletedProperty));
    }

    if (isSelected) {
      dispatch(setPropertyCascaderItems([]));
      dispatch(setSelectedPropertyId(null));
    }

    if (deletedProperty.parentId) {
      const parentProperty = state.property.propertyList.find((property) => {
        return property.id === deletedProperty.parentId;
      }) as IProperty;
      const updatePayload = getPropertyPayload.deletePropertyValue(deletedProperty.displayName, parentProperty);
      const updatedProperty = await propertyTransport.update(updatePayload);
      dispatch(patchPropertyListItem(updatedProperty));
    }

    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setPropertyError);
  } finally {
    dispatch(setIsPropertyLoading(false));
  }
};

export const selectCascaderItemProperty = (payload: IPropertyCascaderItemSelectPropertyPayload) => (dispatch: AppDispatch) => {
  dispatch(changePropertyCascaderItemSelectedPropertyId(payload));
  const state = store.getState();
  const property = state.property.propertyList.find((propertyItem) => propertyItem.id === payload.propertyId);

  if (property) {
    const cascaderItemWithOptions = createCascaderItem.itemWithOptions(property);
    dispatch(addPropertyCascaderItem(cascaderItemWithOptions));
    dispatch(setPropertyCascaderError(null));
  }
};

export const subscribeCascaderItemProperty =
  (payload: IPropertyCascaderItemSubscribePropertyPayload) => async (dispatch: AppDispatch) => {
    const { propertyId, parentId, parentValue, cascaderItemId } = payload;
    const state = store.getState();
    const property = state.property.propertyList.find((propertyItem) => propertyItem.id === propertyId) as IProperty;

    try {
      const updatePayload = getPropertyPayload.addPropertyShowConditionValue(property, parentId, parentValue);
      const updatedProperty = await propertyTransport.update(updatePayload);
      dispatch(patchPropertyListItem(updatedProperty));

      const cascaderItemWithOptions = createCascaderItem.itemWithOptions(property);
      dispatch(changePropertyCascaderItemSelectedPropertyId({ propertyId: property.id, cascaderItemId }));
      dispatch(addPropertyCascaderItem(cascaderItemWithOptions));

      payload.onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error);
    }
  };

export const unsubscribeCascaderItemProperty =
  (payload: IPropertyCascaderItemUnsubscribePropertyPayload) => async (dispatch: AppDispatch) => {
    const { propertyId, parentId, parentValue, cascaderItemId, selectedPropertyId } = payload;
    const state = store.getState();
    const property = state.property.propertyList.find((propertyItem) => {
      return propertyItem.id === propertyId;
    }) as IProperty;

    try {
      const updatePayload = getPropertyPayload.deletePropertyShowConditionValue(property, parentId, parentValue);
      const updatedProperty = await propertyTransport.update(updatePayload);
      dispatch(patchPropertyListItem(updatedProperty));

      if (propertyId === selectedPropertyId) {
        dispatch(filterPropertyCascaderItems(cascaderItemId));
      }
    } catch (error) {
      axiosErrorHandler(error);
    }
  };

export const createCascaderItemProperty =
  (payload: IPropertyCascaderItemCreatePropertyPayload) => async (dispatch: AppDispatch) => {
    const { name, parentId, parentValue, cascaderItemId } = payload;

    try {
      const createPayload = getPropertyPayload.createPropertyWithShowCondition(name, parentId, parentValue);
      const response = await propertyTransport.create(createPayload);
      dispatch(addPropertyListItem(response));

      const cascaderItemWithOptions = createCascaderItem.itemWithOptions(response);
      dispatch(changePropertyCascaderItemSelectedPropertyId({ propertyId: response.id, cascaderItemId }));
      dispatch(addPropertyCascaderItem(cascaderItemWithOptions));

      payload.onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error, (message) => {
        dispatch(setPropertyCascaderItemError({ error: message, cascaderItemId }));
      });
    }
  };

export const updateCascaderItemProperty = (payload: IUpdatePropertyPayload) => async (dispatch: AppDispatch) => {
  try {
    const response = await propertyTransport.update(payload);
    dispatch(patchPropertyListItem(response));

    dispatch(patchPropertyCascaderItemWithOptions(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setPropertyCascaderError);
  }
};

export const updateCascaderItemPropertyDisplayName = (payload: IUpdatePropertyPayload) => async (dispatch: AppDispatch) => {
  const state = store.getState();
  const property = state.property.propertyList.find((propertyItem: any) => {
    return propertyItem.id === payload.id;
  }) as IProperty;

  try {
    if (property.isCategory && property.parentId) {
      const updatedProperty = await propertyTransport.update(payload);
      const parent = state.property.propertyList.find((propertyItem) => {
        return propertyItem.id === property.parentId;
      }) as IProperty;
      const updateParentPayload = getPropertyPayload.updatePropertyValue(
        parent,
        property.displayName,
        payload.displayName as string,
      );
      const updatedParentProperty = await propertyTransport.update(updateParentPayload);

      dispatch(patchPropertyListItemWithParent({ property: updatedProperty, parent: updatedParentProperty }));
      dispatch(
        patchPropertyCascaderItemWithProperties({
          property: updatedProperty,
          parent: updatedParentProperty,
        }),
      );

      payload.onSuccess?.();
    } else {
      const response = await propertyTransport.update(payload);
      dispatch(patchPropertyListItem(response));

      dispatch(patchPropertyCascaderItemWithOptions(response));
      payload.onSuccess?.();
    }
  } catch (error) {
    axiosErrorHandler(error);
  }
};

export const deleteCascaderItemProperty =
  (payload: IPropertyCascaderItemDeletePropertyPayload) => async (dispatch: AppDispatch) => {
    const { propertyId, cascaderItem } = payload;

    try {
      const response = await propertyTransport.delete({ id: propertyId });
      dispatch(removePropertyListItem(response.id));

      if (propertyId === cascaderItem.selectedPropertyId) {
        dispatch(filterPropertyCascaderItems(cascaderItem.id));
        dispatch(setSelectedPropertyId(null));
      }
    } catch (error) {
      axiosErrorHandler(error);
    }
  };

export const selectCascaderItemOption = (payload: IPropertyCascaderItemSelectOptionPayload) => async (dispatch: AppDispatch) => {
  const { valueId, value, cascaderItemId, cascaderItemPropertyId } = payload;

  dispatch(changePropertyCascaderItemSelectedOptionId({ valueId, cascaderItemId }));

  const cascaderItemWithProperties = createCascaderItem.itemWithProperties(cascaderItemPropertyId, value);
  dispatch(addPropertyCascaderItem(cascaderItemWithProperties));
  dispatch(setPropertyCascaderError(null));
};

export const createCascaderItemOption = (payload: IPropertyCascaderItemCreateOptionPayload) => async (dispatch: AppDispatch) => {
  const { value, propertyId, cascaderItemId } = payload;
  const state = store.getState();
  const property = state.property.propertyList.find((propertyItem) => {
    return propertyItem.id === propertyId;
  }) as IProperty;

  try {
    if (property.isCategory) {
      const createPayload = getPropertyPayload.createSubcategory(value, propertyId);
      const createdProperty = await propertyTransport.create(createPayload);
      dispatch(addPropertyListItem(createdProperty));
    }

    const updatePayload = getPropertyPayload.addPropertyValue(value, property);
    const updatedProperty = await propertyTransport.update(updatePayload);
    dispatch(patchPropertyListItem(updatedProperty));

    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, (message) => setPropertyCascaderItemError({ error: message, cascaderItemId }));
  }
};

export const deleteCascaderItemOption = (payload: IPropertyCascaderItemDeleteOptionPayload) => async (dispatch: AppDispatch) => {
  const { valueId, value, propertyId, cascaderItemId, selectedOptionId } = payload;
  const state = store.getState();
  const property = state.property.propertyList.find((propertyItem) => {
    return propertyItem.id === propertyId;
  }) as IProperty;

  try {
    if (property.isCategory) {
      const childProperty = state.property.propertyList.find((propertyItem) => {
        return propertyItem.parentId === propertyId && propertyItem.displayName === value;
      }) as IProperty;
      const deletedProperty = await propertyTransport.delete({ id: childProperty.id });
      dispatch(removePropertyListItem(deletedProperty.id));
      dispatch(removePropertyCascaderItemWithProperties(deletedProperty));
    }

    const updatePayload = getPropertyPayload.deletePropertyValue(value, property);
    const updatedProperty = await propertyTransport.update(updatePayload);
    dispatch(patchPropertyListItem(updatedProperty));
    dispatch(filterPropertyListItemShowConditions({ propertyId, value }));

    if (valueId === selectedOptionId) {
      dispatch(filterPropertyCascaderItems(cascaderItemId));
    }

    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, (message) => setPropertyCascaderItemError({ error: message, cascaderItemId }));
  }
};

export const createCascaderItemWithAllProperties = () => (dispatch: AppDispatch) => {
  const rootCascaderItem = createCascaderItem.itemWithAllProperties();
  dispatch(setPropertyCascaderItems([rootCascaderItem]));
  dispatch(setSelectedPropertyId(null));
};
